import fs from "node:fs/promises";
import path from "node:path";
import { execSync } from "node:child_process";
import { api, listMyOrgs, type Org } from "./api.js";

interface AppRef {
  org: Org;
  appId: string;
  slug: string;
}

interface AppSummary {
  id: string;
  slug: string;
  name?: string;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolve an app id-or-slug against the user's orgs, returning the org +
 * app id/slug. Mirrors the resolution in link.ts so `pull` can be used
 * without a pre-existing `clawnify link`.
 */
export async function resolveApp(idOrSlug: string, orgFilter?: string): Promise<AppRef | null> {
  const orgs = await listMyOrgs();
  if (orgs.length === 0) return null;

  const candidates = orgFilter
    ? orgs.filter((o) => o.id === orgFilter || o.slug === orgFilter)
    : orgs;
  if (candidates.length === 0) return null;

  const isUuid = UUID_RE.test(idOrSlug);
  if (isUuid) {
    for (const org of candidates) {
      const res = await api.get(`/v1/apps/${idOrSlug}`, { orgId: org.id });
      if (res.ok) {
        const app = (await res.json()) as AppSummary;
        return { org, appId: app.id, slug: app.slug };
      }
    }
    return null;
  }

  for (const org of candidates) {
    const res = await api.get(`/v1/apps`, { orgId: org.id });
    if (!res.ok) continue;
    const payload = (await res.json()) as { apps?: AppSummary[] } | AppSummary[];
    const apps = Array.isArray(payload) ? payload : payload.apps ?? [];
    const match = apps.find((a) => a.slug === idOrSlug);
    if (match) return { org, appId: match.id, slug: match.slug };
  }
  return null;
}

/**
 * Fetch the deployed app's current schema.sql snapshot from the API.
 * Returns null if the app has no schema.sql (pre-backfill / no DB).
 *
 * Accepts either a full AppRef (from resolveApp) or a minimal
 * { appId, orgId } pair (from deploy, which already knows both).
 */
export async function fetchSchema(
  ref: AppRef | { appId: string; orgId: string },
): Promise<string | null> {
  const appId = "appId" in ref ? ref.appId : (ref as AppRef).appId;
  const orgId = "orgId" in ref ? ref.orgId : (ref as AppRef).org.id;
  const res = await api.get(`/v1/apps/${appId}/schema`, { orgId });
  if (!res.ok) {
    if (res.status === 404) return null;
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(`Schema fetch failed: ${err.error ?? res.statusText}`);
  }
  const data = (await res.json()) as { schema_sql: string | null };
  return data.schema_sql;
}

export type WriteResult =
  | { status: "written"; path: string; bytes: number }
  | { status: "unchanged"; path: string }
  | { status: "no-schema"; path: string }
  | { status: "refused-uncommitted"; path: string }
  | { status: "refused-different"; path: string };

/**
 * Write a fetched schema to disk with safety guards.
 *
 *   - "unchanged"            : on-disk file already matches what we'd
 *                              write; no-op.
 *   - "refused-uncommitted"  : the target file has uncommitted git
 *                              changes — refuse unless force=true.
 *   - "refused-different"    : the target file differs and we're not
 *                              in a git repo — refuse unless force=true
 *                              (otherwise we'd silently clobber unknown
 *                              local edits).
 *   - "no-schema"            : the deployed app has no schema.sql to
 *                              fetch; nothing written.
 *   - "written"              : wrote the file.
 */
export async function writeSchema(
  targetPath: string,
  schema: string | null,
  options: { force?: boolean } = {},
): Promise<WriteResult> {
  if (schema === null) {
    return { status: "no-schema", path: targetPath };
  }

  let existing: string | null = null;
  try {
    existing = await fs.readFile(targetPath, "utf8");
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code !== "ENOENT") throw err;
  }

  if (existing === schema) {
    return { status: "unchanged", path: targetPath };
  }

  if (existing !== null && !options.force) {
    const dir = path.dirname(path.resolve(targetPath));
    if (isInsideGitRepo(dir)) {
      if (hasUncommittedChanges(dir, targetPath)) {
        return { status: "refused-uncommitted", path: targetPath };
      }
    } else {
      // Outside git, can't tell intent — be conservative.
      return { status: "refused-different", path: targetPath };
    }
  }

  await fs.mkdir(path.dirname(targetPath), { recursive: true });
  await fs.writeFile(targetPath, schema, "utf8");
  return { status: "written", path: targetPath, bytes: schema.length };
}

function isInsideGitRepo(dir: string): boolean {
  try {
    execSync("git rev-parse --is-inside-work-tree", {
      cwd: dir,
      stdio: ["ignore", "pipe", "ignore"],
    });
    return true;
  } catch {
    return false;
  }
}

function hasUncommittedChanges(dir: string, filePath: string): boolean {
  try {
    const relative = path.relative(dir, path.resolve(filePath));
    const status = execSync(`git status --porcelain -- "${relative}"`, {
      cwd: dir,
      stdio: ["ignore", "pipe", "ignore"],
    })
      .toString()
      .trim();
    return status.length > 0;
  } catch {
    // git not available or other error — treat as uncommitted for safety.
    return true;
  }
}
